"use client";

import {
  loginWithEmail,
  loginWithPhoneNumber,
  resendVerificationByEmail,
  resendVerificationByPhone,
} from "@/actions/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useQueryClient } from "@tanstack/react-query";

import { useParams, useSearchParams } from "next/navigation";
import { toast } from "sonner";

import lang from "./lang.json";
import { SupportedLanguage } from "@/lib/shared";
import { resolveLocale } from "@/lib/resolvedLocale";
import { useRouter } from "next/navigation";
import { useFCMToken } from "@/lib/useFCMToken";
import { localizedPath, postAuthRedirectHref } from "@/lib/localePath";
import { broadcastAuthSessionChangedDeferred } from "@/lib/authSession";

type Props = {
  tab: "email" | "phone";
};

// Schema factory functions that accept locale for translated messages
const createEmailSchema = (locale: SupportedLanguage) =>
  z.object({
    email: z.string().email(lang["validation-email-invalid"][locale]).min(2).max(50),
    password: z.string().min(6, lang["validation-password-min"][locale]).max(50),
    role: z.enum(["Personal", "Company"], { message: lang["validation-role-required"][locale] }),
  });

const createPhoneSchema = (locale: SupportedLanguage) =>
  z.object({
    phone: z
      .string()
      .min(10)
      .max(15)
      .regex(/^\+?[0-9]+$/, lang["validation-phone-format"][locale]),
    password: z.string().min(6, lang["validation-password-min"][locale]).max(50),
    role: z.enum(["Personal", "Company"], { message: lang["validation-role-required"][locale] }),
  });

export type EmailFormValues = z.infer<ReturnType<typeof createEmailSchema>>;
export type PhoneFormValues = z.infer<ReturnType<typeof createPhoneSchema>>;

function useLogin({ tab }: Props) {
  const searchParams = useSearchParams();
  const redirectPath = searchParams.get("redirect");
  const contact = searchParams.get("contact");
  const type = searchParams.get("type");
  const roleParam = searchParams.get("role");

  const router = useRouter();
  const params = useParams();
  const locale = resolveLocale(params.locale as string | undefined);
  const queryClient = useQueryClient();

  const [isLoading, isLoadingSet] = useState(false);

  // Get FCM token for push notifications
  const { token: fcmToken } = useFCMToken();

  // Create validation schemas with locale
  const emailSchema = createEmailSchema(locale);
  const phoneSchema = createPhoneSchema(locale);

  // Create the appropriate form based on the tab
  const emailForm = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: type === "email" && contact ? contact : "",
      password: "",
      role:
        roleParam === "Personal" || roleParam === "Company"
          ? roleParam
          : undefined,
    },
  });

  const phoneForm = useForm<PhoneFormValues>({
    resolver: zodResolver(phoneSchema),
    defaultValues: {
      phone: type === "phone" && contact ? contact : "",
      password: "",
      role:
        roleParam === "Personal" || roleParam === "Company"
          ? roleParam
          : undefined,
    },
  });

  // Use the correct form based on the tab
  const form = tab === "email" ? emailForm : phoneForm;

  // Type-safe submit handlers for each form type
  const handleEmailSubmit = async (values: EmailFormValues) => {
    isLoadingSet(true);
    const response = await loginWithEmail(
      {
        email: values.email,
        password: values.password,
        role: values.role,
        deviceToken: fcmToken || undefined,
      },
      redirectPath || undefined
    );

    if (response.status === "success") {
      const successResponse = response as typeof response & {
        redirectPath?: string;
      };
      await queryClient.invalidateQueries({ queryKey: ["myProfile"] });
      const target = postAuthRedirectHref(
        locale,
        successResponse.redirectPath ?? redirectPath,
      );
      router.push(target);
      broadcastAuthSessionChangedDeferred();
      return;
    }

    if (response.status === "error") {
      if (response.errorType === "account-not-verified") {
        const resResend = await resendVerificationByEmail({
          email: values.email,
          role: values.role,
        });

        if (resResend.status === "success") {
          toast.error(lang["messages.email_not_verified"][locale]);
          router.push(
            localizedPath(
              locale,
              `/auth/otp?contact=${encodeURIComponent(values.email)}&user_id=${resResend.metaData.userId}&type=email&from=register&role=${values.role}`,
            ),
          );
        }

        return;
      } else if (response.message === "message.email_has_already_been_taken.") {
        toast.error(lang["message.email_has_already_been_taken."][locale]);
        return;
      }

      toast.error(response.message);
    }

    isLoadingSet(false);
  };

  const handlePhoneSubmit = async (values: PhoneFormValues) => {
    isLoadingSet(true);
    const response = await loginWithPhoneNumber(
      {
        phone: values.phone,
        password: values.password,
        role: values.role,
        deviceToken: fcmToken || undefined,
      },
      redirectPath || undefined
    );

    if (response.status === "success") {
      const successResponse = response as typeof response & {
        redirectPath?: string;
      };
      await queryClient.invalidateQueries({ queryKey: ["myProfile"] });
      const target = postAuthRedirectHref(
        locale,
        successResponse.redirectPath ?? redirectPath,
      );
      router.push(target);
      broadcastAuthSessionChangedDeferred();
      return;
    }

    if (response.status === "error") {
      if (response.errorType === "account-not-verified") {
        const resResend = await resendVerificationByPhone({
          phone: values.phone,
          role: values.role,
        });

        if (resResend.status === "success") {
          toast.error(lang["messages.phone_not_verified"][locale]);
          router.push(
            localizedPath(
              locale,
              `/auth/otp?contact=${encodeURIComponent(values.phone)}&user_id=${resResend.metaData.userId}&type=phone&from=register`,
            ),
          );
        }

        return;
      } else if (response.message === "message.phone_has_already_been_taken.") {
        toast.error(lang["message.phone_has_already_been_taken."][locale]);
        return;
      }

      toast.error(response.message);
    }

    isLoadingSet(false);
  };

  // Unified onSubmit function that delegates to the appropriate handler
  const onSubmit = tab === "email" ? handleEmailSubmit : handlePhoneSubmit;

  return {
    form,
    onSubmit,
    isLoading,
  };
}

export default useLogin;
